/* eslint-disable */
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { getAuth, signOut as fbSignOut } from "firebase/auth";
import { doc, onSnapshot, getDoc } from "firebase/firestore";
import { ensureFirebase } from "services/firebase";
import * as membersService from "services/membersService";

// ───────── Local Storage Keys ─────────
const LS_MEMBER_ID = "auth.memberId";
const LS_PHONE_E164 = "auth.phoneE164";

export function setLocalMemberKeys(memberId, phoneE164) {
    try {
        if (memberId) localStorage.setItem(LS_MEMBER_ID, memberId);
        if (phoneE164) localStorage.setItem(LS_PHONE_E164, phoneE164);
    } catch { }
}
export function clearLocalMemberKeys() {
    try {
        localStorage.removeItem(LS_MEMBER_ID);
        localStorage.removeItem(LS_PHONE_E164);
    } catch { }
}
export function getLocalMemberId() {
    try { return localStorage.getItem(LS_MEMBER_ID) || null; } catch { return null; }
}
export function getLocalPhoneE164() {
    try { return localStorage.getItem(LS_PHONE_E164) || null; } catch { return null; }
}

// ───────── Context ─────────
const MemberCtx = createContext({
    ready: false,
    memberId: null,
    member: null,
    refresh: async () => { },
    signOut: async () => { },
    setLocalMemberKeys,  // helper 노출(원하면)
    clearLocalMemberKeys,
});

export function MemberProvider({ children }) {
    const [ready, setReady] = useState(false);
    const [memberId, setMemberId] = useState(null);
    const [member, setMember] = useState(null);

    const unsubRef = useRef(null);
    const resolvingRef = useRef(false);

    const { db } = ensureFirebase();

    // ── 구독 정리
    const stopSubscribe = useCallback(() => {
        try { unsubRef.current && unsubRef.current(); } catch { }
        unsubRef.current = null;
    }, []);

    // ── memberId로 스냅샷 시작
    const subscribeMember = useCallback((mid) => {
        stopSubscribe();
        if (!mid) return;
        const ref = doc(db, "members", mid);
        unsubRef.current = onSnapshot(
            ref,
            (snap) => {
                setMember(snap.exists() ? { id: snap.id, ...snap.data() } : null);
                setReady(true);
            },
            (err) => {
                console.warn("[MemberContext] snapshot error:", err);
                setMember(null);
                setReady(true);
            }
        );
    }, [db, stopSubscribe]);

    // ── 초기 해석 & 구독 시작 (로컬키 기반)
    const resolveAndSubscribe = useCallback(async () => {
        if (resolvingRef.current) return;
        resolvingRef.current = true;

        try {
            let mid = getLocalMemberId();
            let phone = getLocalPhoneE164();

            // 1) memberId가 있으면 바로 구독
            if (mid) {
                // 존재 확인은 스냅샷에서 처리
                setMemberId(mid);
                subscribeMember(mid);
                return;
            }

            // 2) memberId 없고 phone만 있으면 1회 해석
            if (!mid && phone) {
                try {
                    const found = await membersService.getMemberIdByPhone(phone);
                    if (found) {
                        setLocalMemberKeys(found, phone);
                        setMemberId(found);
                        subscribeMember(found);
                        return;
                    }
                } catch (e) {
                    console.warn("[MemberContext] resolve by phone failed:", e);
                }
            }

            // 3) 둘 다 없으면 준비 완료(비회원 상태)
            setMember(null);
            setReady(true);
        } finally {
            resolvingRef.current = false;
        }
    }, [subscribeMember]);

    // ── 강제 새로고침 (로컬키 재해석 + 단발 getDoc → 구독 재설정)
    const refresh = useCallback(async () => {
        stopSubscribe();
        let mid = getLocalMemberId();
        let phone = getLocalPhoneE164();

        // 로컬 mid가 있으면 한 번 존재 확인(선택)
        if (mid) {
            try {
                const snap = await getDoc(doc(db, "members", mid));
                if (!snap.exists()) {
                    // 로컬 mid가 유효하지 않으면 phone으로 재해석
                    mid = null;
                }
            } catch {
                // 네트워크 오류면 기존 구독 그대로 복구
            }
        }

        if (!mid && phone) {
            const found = await membersService.getMemberIdByPhone(phone).catch(() => null);
            if (found) {
                setLocalMemberKeys(found, phone);
                mid = found;
            }
        }

        setMemberId(mid || null);
        if (mid) subscribeMember(mid);
        else { setMember(null); setReady(true); }
    }, [db, stopSubscribe, subscribeMember]);

    // ── 로그아웃(선택 제공): Firebase 세션 + 로컬키 초기화
    const signOut = useCallback(async () => {
        try { await fbSignOut(getAuth()); } catch { }
        clearLocalMemberKeys();
        stopSubscribe();
        setMemberId(null);
        setMember(null);
        setReady(true);
    }, [stopSubscribe]);

    // 부팅 시 1회
    useEffect(() => {
        resolveAndSubscribe();
        return () => stopSubscribe();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 포그라운드 복귀 시 한 번 갱신(선택)
    useEffect(() => {
        const onVis = () => { if (document.visibilityState === "visible") refresh(); };
        document.addEventListener("visibilitychange", onVis);
        return () => document.removeEventListener("visibilitychange", onVis);
    }, [refresh]);

    const value = {
        ready,
        memberId,
        member,
        refresh,
        signOut,
        setLocalMemberKeys,
        clearLocalMemberKeys,
    };

    return <MemberCtx.Provider value={value}>{children}</MemberCtx.Provider>;
}

export function useMember() {
    return useContext(MemberCtx);
}
